import React, { useEffect } from 'react';
import { motion } from 'motion/react';
import { Calendar, User, ArrowLeft, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const fadeUp = {
  hidden: { opacity: 0, y: 30, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] } },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 1, 0.5, 1] } }
};

export default function CustomApps() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="pt-32 pb-24 min-h-screen bg-black text-white relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-900/20 rounded-full blur-[120px] pointer-events-none z-0" />
      
      <div className="max-w-4xl w-full mx-auto px-4 sm:px-8 relative z-10">
        
        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-8">
          <Link to="/blogs" className="inline-flex items-center text-sm font-body text-white/50 hover:text-white transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blogs
          </Link>
        </motion.div>

        <motion.div variants={fadeUp} initial="hidden" animate="visible" className="mb-12">
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="px-3 py-1 text-xs font-medium rounded-full bg-white/10 text-white font-body border border-white/5">
              Custom Apps
            </span>
            <div className="flex items-center gap-1.5 text-xs text-white/40 font-body">
              <Calendar className="w-3.5 h-3.5" />
              April 10, 2026
            </div>
            <div className="flex items-center gap-1.5 text-xs text-white/40 font-body">
              <User className="w-3.5 h-3.5" />
              EsyAI Team
            </div>
          </div>
          
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white leading-[1.1] mb-6">
            Custom Apps That Help Your Business Grow: The EsyAI Advantage
          </h1>
          
          <p className="text-xl text-white/60 font-body font-light leading-relaxed">
            Find out how custom-built AI apps give your business a competitive edge, streamlining operations, improving customer experience, and unlocking new revenue.
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
          className="w-full aspect-[16/9] md:aspect-[21/9] rounded-3xl overflow-hidden mb-16 border border-white/10 liquid-glass shadow-2xl relative"
        >
          <img 
            src="/blogs/blog-3-new.png" 
            alt="Custom App Concept" 
            className="w-full h-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
        </motion.div>

        <motion.article 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.25, 1, 0.5, 1] }}
          className="prose prose-invert prose-lg max-w-none font-body text-white/70 prose-headings:font-heading prose-headings:text-white prose-a:text-purple-400 hover:prose-a:text-purple-300 prose-p:leading-loose"
        >
          <p className="lead text-xl text-white/90">
            Off-the-shelf software is designed for everyone, which means it's perfectly tailored for no one. As your business grows, generic tools create friction, workarounds, and inefficiency. Custom apps, built specifically for your business, solve your exact problems and unlock growth that one-size-fits-all software never can.
          </p>
          
          <p>
            At EsyAI, we design and build bespoke AI-powered apps for businesses across the UK. Here's why custom apps are a game-changer.
          </p>

          <h2 className="text-2xl mt-12 mb-6">What Is a Custom Business App?</h2>
          <p>
            A custom business app is software designed and built exclusively for your organisation. It could be a customer-facing portal, an internal management tool, a mobile app, a booking system, a reporting dashboard, or an AI assistant, engineered precisely to match your workflows, branding, and goals.
          </p>

          <h2 className="text-2xl mt-12 mb-6">1. Tailored to Your Exact Workflows</h2>
          <p>
            Generic software forces your team to adapt their processes to fit the tool. A custom app does the opposite. It's shaped around how your business actually works. The result is faster adoption, higher productivity, and far less friction.
          </p>
          <p>
            When your tools fit your team perfectly, efficiency skyrockets. Custom apps eliminate the workarounds that drain hours every week.
          </p>

          <h2 className="text-2xl mt-12 mb-6">2. Competitive Advantage</h2>
          <p>
            Your competitors are using the same off-the-shelf tools you are. A custom app is yours alone. No competitor can replicate it. Whether it's a unique customer experience, a faster internal process, or a smarter data workflow, custom software gives you an edge that is genuinely difficult to copy.
          </p>

          <h2 className="text-2xl mt-12 mb-6">3. Scale as You Grow</h2>
          <p>
            Generic software often hits its limits as your business grows, with user caps, storage limits, and feature restrictions. Custom apps are built to scale with you. As your team grows or your needs evolve, your app evolves with it.
          </p>
          <ul className="list-disc pl-6 space-y-2 my-6 marker:text-purple-500">
            <li>No arbitrary user or data limits</li>
            <li>Features added exactly when you need them</li>
            <li>Integrates with your existing tools and systems</li>
          </ul>

          <h2 className="text-2xl mt-12 mb-6">4. AI-Powered Intelligence Built In</h2>
          <p>
            EsyAI builds custom apps with artificial intelligence embedded from the ground up. This means your app can learn from data, make predictions, automate complex decisions, and surface insights that generic software simply cannot provide.
          </p>
          <p>Examples of AI features we build into custom apps:</p>
          <ul className="list-disc pl-6 space-y-2 my-6 marker:text-purple-500">
            <li>Intelligent customer segmentation and personalisation</li>
            <li>Automated lead scoring and prioritisation</li>
            <li>Predictive stock or demand forecasting</li>
            <li>Natural language interfaces and AI chatbots</li>
            <li>Real-time anomaly detection and alerts</li>
          </ul>

          <h2 className="text-2xl mt-12 mb-6">5. Full Ownership and Control</h2>
          <p>
            With off-the-shelf software, you're at the mercy of the vendor's pricing, updates, and decisions. With a custom app, you own the software entirely. No subscription hikes, no forced feature changes, no dependency on a third party's roadmap.
          </p>

          <h2 className="text-2xl mt-12 mb-6">6. Better Customer Experiences</h2>
          <p>
            Customer-facing custom apps, portals, booking systems, and mobile apps, can be designed to deliver exceptional user experiences that reflect your brand. A seamless, intuitive app builds loyalty, increases retention, and drives referrals.
          </p>

          <h2 className="text-2xl mt-12 mb-6">Real Examples of Custom Apps EsyAI Can Build</h2>
          <ul className="list-disc pl-6 space-y-2 my-6 marker:text-purple-500">
            <li>Client portals with real-time project updates</li>
            <li>AI-powered booking and scheduling systems</li>
            <li>Custom CRM tools built around your sales process</li>
            <li>Internal knowledge bases with AI search</li>
            <li>Automated reporting and analytics dashboards</li>
            <li>Multi-platform mobile apps for iOS and Android</li>
          </ul>

          <hr className="my-12 border-white/10" />

          <h2 className="text-2xl mb-6">Invest in Software That's Yours</h2>
          <p>
            In a world where every business has access to the same generic tools, your custom app is your secret weapon. It's the difference between fitting into a mould and building something that fits your business perfectly.
          </p>

          <div className="not-prose mt-12 p-8 liquid-glass rounded-3xl border border-white/10 bg-gradient-to-br from-purple-900/20 to-transparent">
            <h3 className="text-xl font-heading text-white mb-3">Transform the way you do business</h3>
            <p className="text-white/70 mb-6 text-base font-body">
              EsyAI combines AI expertise with bespoke software development to deliver apps that don't just work. They transform the way you do business.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-black font-medium text-sm rounded-full hover:bg-white/90 transition-colors font-body"
            >
              Start the conversation <ArrowUpRight className="w-4 h-4 ml-2" />
            </Link>
          </div>

        </motion.article>
      </div>
    </div>
  );
}
